import React, { useContext, useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import Input from "../../../components/Atomos/Inputs/Input";
import {
  openInvoicePDF,
  downloadInvoicePDF,
  generateInvoicePDFWithLogo,
  saveInvoicePDFToBlob,
} from "../../../utils/generateInvoicePDF";
import { IEvent } from "../../../interfaces/models/IEvent";
import { IRegistration } from "../../../interfaces/models/IRegistration";
import useDataLoader2 from "../../../hooks/useDataLoader2";
import { IPayment } from "../../../interfaces/models/IPayment";
import CalendarInput from "../../../components/Atomos/Inputs/CalendarInput";
import TextArea from "../../../components/Atomos/Inputs/TextArea";
import IFirm, { IFirmAddress } from "../../../interfaces/models/IFirm";
import { updateRegistration } from "../../../Services/Registration";
import SaveChanges from "../../../components/Atomos/Icons/EditMode/SaveChanges";
import { toastError, toastPromise } from "../../../utils/toast";
import { formatDateToString } from "../../../utils/functions";
import {
  IInvoiceHistory,
  saveInvoiceHistory,
  sendInvoice,
} from "../../../Services/Invoice";
import { paymentMethodEnum } from "../../../Enums/RegistrationEnum";
import {
  calculateInvoiceAmountDue,
  calculateInvoiceTotalAmount,
  determinePaymentStatus,
  generateInvoiceFilename,
  getInvoiceTypeForEmail,
  getPaymentStatusForHistory,
  isInvoicePrepaidRegistration,
  parseAdditionalFee,
  resolveInvoiceAmountPaid,
  resolveRegistrationDateSec,
} from "../../../utils/invoiceCalculations";
import { getAllPaymentsByRegistrationId } from "../../../Services/Payment";
import {
  selectEditParticipantForm,
  selectPayments,
} from "../EditParticipant/editParticipant.selectors";
import {
  applyPrepaidFlagFromLedger,
  hasPrepaidPaymentInLedger,
} from "../EditParticipant/utils/participantPaymentRules";
import { normalizeRegistrationGuests } from "../../../utils/registrationGuests";
import { getAccountingMailingAddressForInvoice } from "../../../utils/firmInvoiceAddress";
import PaginatedTable from "../../../components/Organismos/Table/PaginatedTable";
import AuthContext from "../../../store/AuthContext";
import { formatStaffActorLabel } from "../../../utils/auditActorDisplay";

interface SendEmailInvoiceTabProps {
  event: IEvent;
  registration: IRegistration;
  payment: IPayment | undefined;
  firm?: IFirm;
  /** Reload registration (and related data) in the parent so other tabs see server state. */
  onRegistrationRefresh?: () => Promise<void>;
}

function resolveFirmDisplayName (
  firm: IFirm | undefined,
  registration: IRegistration,
): string {
  return firm?.firmName || firm?.name || registration.firmName || "";
}

type InvoiceType = "Paid Invoice" | "Due Invoice" | "No Charge Confirmation";

function getDocAmountPaid (registration: IRegistration): number {
  const v = registration.amountPaid;
  return typeof v === "number" && !Number.isNaN(v) ? v : 0;
}

const roundAmountPaid = (n: number) => Math.round(n * 100) / 100;

function buildRegistrationForInvoice (
  registration: IRegistration,
  formGuests: IRegistration["guests"],
  amountPaid: number,
  payments: Array<{ paymentMethod?: string; amount?: number }> | null | undefined,
): IRegistration {
  const guestsForInvoice = normalizeRegistrationGuests({
    guests:
      formGuests && formGuests.length > 0 ? formGuests : registration.guests,
    guestName: registration.guestName,
    guestName2: registration.guestName2,
    registrationDate: resolveRegistrationDateSec(registration),
  });
  return applyPrepaidFlagFromLedger(
    {
      ...registration,
      guests: guestsForInvoice,
      amountPaid,
    },
    payments,
  );
}

type TableCellProps = {
  rowIndex: number;
  row: IInvoiceHistory;
  value: unknown;
};

export default function SendEmailInvoiceTab ({
  event,
  registration,
  payment,
  firm,
  onRegistrationRefresh,
}: SendEmailInvoiceTabProps): React.ReactElement {
  const generateReferenceNumber = (): string => {
    if (!event.id || !registration.personId) {
      return "";
    }
    const eventNumericDigits = event.id.replace(/\D/g, "");
    const eventPrefix = eventNumericDigits.substring(0, 4).padStart(4, "0");
    const userNumericDigits = registration.personId.replace(/\D/g, "");
    const userPrefix = userNumericDigits.substring(0, 4).padStart(4, "0");
    return `M${eventPrefix}-${userPrefix}`;
  };

  const [ccName, setCcName] = useState<string>("");
  const [ccEmail, setCcEmail] = useState<string>("");
  const [paymentAdjustment, setPaymentAdjustment] = useState<string>("");
  const [paymentAdjustmentError, setPaymentAdjustmentError] = useState(false);
  const [paymentAdjustmentErrorMsj, setPaymentAdjustmentErrorMsj] =
    useState("");
  const [savedAmountPaid, setSavedAmountPaid] = useState<number>(() =>
    getDocAmountPaid(registration),
  );
  const [invoiceDate, setInvoiceDate] = useState<string>(
    formatDateToString(new Date()),
  );
  const [reference, setReference] = useState<string>(generateReferenceNumber());
  const [billTo, setBillTo] = useState<string>();
  const [remitTo] = useState<string>(
    "ATTN: Accounting Department\nWorld Services Group, Inc.\n2777 Allen Pkwy., Suite 622\nHouston, Texas 77019",
  );
  const [personName, setPersonName] = useState<string>(
    `${registration.personName || ""} ${registration.personLastName || ""}`.trim(),
  );
  const [firmName, setFirmName] = useState<string>(() =>
    resolveFirmDisplayName(firm, registration),
  );
  const [email, setEmail] = useState<string>(registration.personEmail || "");
  const { tokenDecoded, loggedPerson } = useContext(AuthContext);
  const ledgerPaymentsFromStore = useSelector(selectPayments);
  const formGuests = useSelector(selectEditParticipantForm).guests;

  const getSavedBy = (): string =>
    formatStaffActorLabel(loggedPerson?.name, loggedPerson?.lastName);

  const getSavedById = (): string | null => loggedPerson?.id?.trim() || null;

  const endpoint = "/invoice-history";
  const { data, fetchData, sort, setSort, pagination, setPagination } =
    useDataLoader2<IInvoiceHistory>({
      endpoint,
      defaultPageSize: 20,
      defaultSorting: { field: "registrationDate", order: "desc" },
      persistentFilter: {
        registrationId: { value: registration.id, comparisonType: "EQUAL" },
      },
    });

  const getInvoiceType = (payment: string): InvoiceType => {
    if (payment === "completed") {
      return "Paid Invoice";
    } else if (payment === "pending") {
      return "Due Invoice";
    } else {
      return "No Charge Confirmation";
    }
  };

  useEffect(() => {
    setSavedAmountPaid(getDocAmountPaid(registration));
    setPaymentAdjustment("");
    setPaymentAdjustmentError(false);
    setPaymentAdjustmentErrorMsj("");
  }, [registration.id, registration.amountPaid]);

  const handleViewInvoice = async (
    invoiceUrl: string | undefined | null,
    filename?: string,
  ) => {
    if (!invoiceUrl || invoiceUrl.trim() === "") {
      console.error("Invoice URL is not available");
      toastError("Invoice file is not available to view.");
      return;
    }

    try {
      const downloadFilename = filename || "invoice.pdf";
      await Promise.all([
        downloadInvoicePDF(invoiceUrl, downloadFilename),
        openInvoicePDF(invoiceUrl, filename),
      ]);
    } catch (error: any) {
      console.error("Error opening invoice:", error);
      toastError(
        `Failed to open invoice: ${error?.message || "Unknown error"}. Please try again.`,
      );
    }
  };

  const invoiceHistoryColumns = useMemo(
    () => [
      {
        id: "count",
        header: "#",
        enableSorting: false,
        cell: ({ rowIndex }: TableCellProps) => (
          <p>{rowIndex + 1 + pagination.pageIndex * pagination.pageSize}</p>
        ),
      },
      { id: "type", header: "Invoice Type", enableSorting: false },
      { id: "dateSent", header: "Date Sent" },
      { id: "amount", header: "Amount" },
      { id: "currency", header: "Currency" },
      { id: "cc", header: "Cc." },
      { id: "sentBy", header: "Sent By" },
      {
        id: "invoiceUrl",
        header: "Invoice",
        enableSorting: false,
        cell: ({ row }: TableCellProps) => {
          const invoiceUrl = row.invoiceUrl;
          const invoiceFilename = row.invoice;
          if (!invoiceUrl) {
            return <span>-</span>;
          }
          return (
            <button
              onClick={() => handleViewInvoice(invoiceUrl, invoiceFilename)}
              className="underline cursor-pointer text-blue-600 hover:text-blue-800 bg-transparent border-none p-0"
              type="button"
            >
              View Invoice
            </button>
          );
        },
      },
    ],
    [pagination.pageIndex, pagination.pageSize],
  );

  const handleUpdateAmount = async () => {
    const adjustment = parseFloat(paymentAdjustment);
    if (Number.isNaN(adjustment) || adjustment === 0) {
      setPaymentAdjustmentError(true);
      setPaymentAdjustmentErrorMsj("Enter an amount to add or subtract.");
      toastError("Enter an amount to add or subtract.");
      return;
    }
    if (!registration.id) {
      toastError("Registration data missing. Cannot update amount paid.");
      return;
    }

    const current = roundAmountPaid(savedAmountPaid);
    const newTotal = roundAmountPaid(current + adjustment);
    if (newTotal < 0) {
      setPaymentAdjustmentError(true);
      setPaymentAdjustmentErrorMsj("Amount paid cannot be negative.");
      toastError("Amount paid cannot be negative.");
      return;
    }

    setPaymentAdjustmentError(false);
    setPaymentAdjustmentErrorMsj("");

    try {
      await toastPromise(
        updateRegistration({
          ...registration,
          id: registration.id,
          amountPaid: newTotal,
          staffInvoiceAmountPaidOnly: true,
          guestActionBy: getSavedBy(),
          guestActionById: getSavedById(),
        }),
        {
          pending: "Updating amount paid...",
          success: "Amount paid updated.",
          error: (error: any) =>
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            "Failed to update amount paid.",
        },
      );
      setPaymentAdjustment("");
      try {
        await onRegistrationRefresh?.();
      } catch (refreshError) {
        console.error(
          "Error refreshing registration after amount paid update:",
          refreshError,
        );
        toastError(
          "Amount saved but reloading registration failed. Refresh the page to see latest data.",
        );
      }
    } catch (error) {
      console.error("Error updating amount paid:", error);
    }
  };

  const formatAddress = (
    address: IFirmAddress | string | null | undefined,
  ): string => {
    if (!address) {
      return "";
    }
    if (typeof address === "string") {
      return address;
    }

    const parts: string[] = [];
    if (address.address1) {
      parts.push(address.address1);
    }
    if (address.address2) {
      parts.push(address.address2);
    }
    if (address.city) {
      if (address.state && address.zipCode) {
        parts.push(`${address.city}, ${address.state} ${address.zipCode}`);
      } else if (address.zipCode) {
        parts.push(`${address.city} ${address.zipCode}`);
      } else {
        parts.push(address.city);
      }
    } else if (address.state) {
      parts.push(address.state);
    }
    if (address.country) {
      parts.push(address.country);
    }

    return parts.join("\n");
  };

  const totalDue = useMemo(() => {
    const payments = ledgerPaymentsFromStore ?? [];
    const additionalFee = parseAdditionalFee(
      (registration as any).additionalFee,
    );
    const registrationForInvoice = buildRegistrationForInvoice(
      registration,
      formGuests,
      savedAmountPaid,
      payments,
    );
    const prepaidInLedger = hasPrepaidPaymentInLedger(payments);
    return calculateInvoiceAmountDue(
      event,
      registrationForInvoice,
      additionalFee,
      { hasPrepaidPaymentInLedger: prepaidInLedger, payments },
    );
  }, [
    savedAmountPaid,
    event,
    registration,
    formGuests,
    ledgerPaymentsFromStore,
  ]);

  const generateInvoiceData = async () => {
    if (!registration.id || !event.id || !registration.personId) {
      return null;
    }

    const additionalFeeValue = parseAdditionalFee(
      (registration as any).additionalFee,
    );
    const fetchedPayments = await getAllPaymentsByRegistrationId(registration.id);
    const payments =
      fetchedPayments.length > 0
        ? fetchedPayments
        : ledgerPaymentsFromStore ?? [];
    const prepaidInLedger = hasPrepaidPaymentInLedger(payments);
    const registrationForInvoice = buildRegistrationForInvoice(
      registration,
      formGuests,
      savedAmountPaid,
      payments,
    );
    const invoicePrepaid = isInvoicePrepaidRegistration(registrationForInvoice, {
      hasPrepaidPaymentInLedger: prepaidInLedger,
    });
    const invoicePaidAmount = resolveInvoiceAmountPaid(registrationForInvoice, {
      hasPrepaidPaymentInLedger: prepaidInLedger,
      payments,
    });
    const totalInvoiceAmount = calculateInvoiceTotalAmount(
      event,
      registrationForInvoice,
      additionalFeeValue,
      { hasPrepaidPaymentInLedger: prepaidInLedger },
    );
    const paid = determinePaymentStatus(totalInvoiceAmount, invoicePaidAmount);
    const filename = generateInvoiceFilename(event, personName, paid);

    const invoiceAddressData: IFirmAddress | undefined =
      getAccountingMailingAddressForInvoice(firm);
    const paymentMethod =
      (registrationForInvoice.paymentMethod as any) ||
      (payment?.paymentMethod as any) ||
      "creditCard";
    const paymentMethodOptions = Object.entries(paymentMethodEnum).map(
      ([key, value]) => ({
        id: key,
        name: value,
      }),
    );

    const pdfBlob = await generateInvoicePDFWithLogo({
      event: {
        id: event?.id,
        title: event?.title,
        startDate: event?.startDate,
        endDate: event?.endDate,
        location: event?.location,
        fee: event?.fee,
        program: event?.program,
      },
      person: {
        name: personName,
        email: email,
      },
      firm: firm ? { name: firmName } : null,
      badgeName: registration.badgeName,
      guests: registrationForInvoice.guests,
      guestName: registrationForInvoice.guestName,
      guestName2: registrationForInvoice.guestName2,
      eventPayment: invoicePaidAmount.toString(),
      creditPayment: "0",
      amountRefunded: "0",
      additionalFee: additionalFeeValue.toString(),
      paymentMethod,
      paymentMethodOptions,
      paid,
      notes: undefined,
      eventId: event.id,
      registrationId: registration.id,
      personId: registration.personId,
      filename,
      invoiceAddress: invoiceAddressData,
      selectedSpecialEventIds: registrationForInvoice.selectedSpecialEventIds ?? [],
      specialEventGuestNames: registration.specialEventGuestNames ?? {},
      registrationDate: resolveRegistrationDateSec(registrationForInvoice),
      paymentStatus: registrationForInvoice.paymentStatus,
      attendeeType: registrationForInvoice.attendeeType,
      hasPrepaidPaymentInLedger: prepaidInLedger,
      isPrepaid: invoicePrepaid,
      isHostFirm:
        registration.isHostFirm === true ||
        registration.paymentStatus === "paidSPR" ||
        registration.paymentStatus === "unpaidSPR",
    });

    return { pdfBlob, filename, totalInvoiceAmount, paid, paymentMethod };
  };

  const handlePreviewInvoice = async () => {
    try {
      const invoiceData = await generateInvoiceData();
      if (!invoiceData || !invoiceData.pdfBlob) {
        toastPromise(
          Promise.reject(new Error("Failed to generate invoice PDF")),
          {
            error: "Failed to generate invoice. Please try again.",
          },
        );
        return;
      }

      await Promise.all([
        downloadInvoicePDF(invoiceData.pdfBlob, invoiceData.filename),
        openInvoicePDF(invoiceData.pdfBlob, invoiceData.filename),
      ]);
    } catch (error: any) {
      console.error("Error previewing invoice:", error);
      toastPromise(Promise.reject(error), {
        error: error?.message || "Failed to preview invoice. Please try again.",
      });
    }
  };

  const handleSendInvoice = async () => {
    if (!registration.id || !event.id || !registration.personId) {
      toastPromise(Promise.reject(new Error("Missing required information")), {
        error:
          "Missing required information. Please check registration and event data.",
      });
      return;
    }

    try {
      const invoiceData = await generateInvoiceData();
      if (!invoiceData || !invoiceData.pdfBlob) {
        toastPromise(
          Promise.reject(new Error("Failed to generate invoice PDF")),
          {
            error: "Failed to generate invoice. Please try again.",
          },
        );
        return;
      }

      let savedPdfPath: string | null = null;
      const storagePathOrUrl = await saveInvoicePDFToBlob(
        invoiceData.pdfBlob,
        invoiceData.filename,
        registration.personId,
        event.id,
      );

      if (storagePathOrUrl) {
        if (storagePathOrUrl.startsWith("http")) {
          savedPdfPath = storagePathOrUrl;
        } else {
          const storageAccount =
            process.env.REACT_APP_STORAGE_ACCOUNT_NAME ||
            process.env.REACT_APP_STORAGE_ACCOUNT ||
            "wsgfilestorageqa";
          const container =
            process.env.REACT_APP_STORAGE_ACCOUNT_CONTAINER_NAME ||
            process.env.REACT_APP_STORAGE_CONTAINER ||
            "testing";
          const cleanStoragePath = storagePathOrUrl.startsWith("/")
            ? storagePathOrUrl.substring(1)
            : storagePathOrUrl;
          savedPdfPath = `https://${storageAccount}.blob.core.windows.net/${container}/${cleanStoragePath}`;
        }
      }

      const invoiceTypeForSend = getInvoiceTypeForEmail(invoiceData.paid);
      const paymentStatusForHistory = getPaymentStatusForHistory(
        invoiceData.paid,
      );

      const currency = event?.fee?.currency || payment?.currency || "USD";

      await toastPromise(
        sendInvoice({
          registrationId: registration.id,
          eventId: event.id,
          invoiceType: invoiceTypeForSend,
          ccName: ccName.trim() || undefined,
          ccEmail: ccEmail.trim() || undefined,
          attendeeEmail: email || undefined,
          currency: currency,
          pdfPath: savedPdfPath || undefined,
          amount: invoiceData.totalInvoiceAmount,
          paymentStatus: paymentStatusForHistory,
          filename: invoiceData.filename,
        }),
        {
          pending: "Sending invoice email...",
          success: "Invoice email sent successfully!",
          error: (error: any) =>
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            "Failed to send invoice email. Please try again.",
        },
      );

      try {
        await saveInvoiceHistory({
          registrationId: registration.id,
          eventId: event.id,
          currency: currency,
          amount: invoiceData.totalInvoiceAmount,
          paymentStatus: paymentStatusForHistory,
          pdfPath: savedPdfPath || undefined,
          filename: invoiceData.filename,
          ccEmail: ccEmail.trim() || undefined,
          ccName: ccName.trim() || undefined,
        });

        fetchData();
      } catch (historyError: any) {
        console.error("Error saving invoice history:", historyError);
      }
    } catch (error: any) {
      console.error("Error sending invoice:", error);
    }
  };

  useEffect(() => {
    setReference(generateReferenceNumber());
  }, [event.id, registration.personId]);

  useEffect(() => {
    setFirmName(resolveFirmDisplayName(firm, registration));
  }, [firm, registration.firmName, registration.id]);

  useEffect(() => {
    if (!firm) {
      setBillTo("");
      return;
    }
    const acct = getAccountingMailingAddressForInvoice(firm);
    setBillTo(acct ? formatAddress(acct) : "");
  }, [firm]);

  const formatData = (dataToFormat: IInvoiceHistory[]) => {
    if (!dataToFormat || !Array.isArray(dataToFormat)) {
      return [];
    }
    return dataToFormat.map((d: IInvoiceHistory) => ({
      ...d,
      dateSent: d.dateSent ? formatDateToString(new Date(d.dateSent)) : "-",
      cc: d.cc ?? "-",
      amount: d.amount.toFixed(2) ?? "0.00",
    }));
  };

  return (
    <div className="events-content-container" style={{ padding: 0 }}>
      <form className="w-full">
        <h1 className="font-decimal text-[32px] not-italic font-medium leading-normal text-Default mx-1">
          {getInvoiceType(registration.paymentStatus || "")}
        </h1>
        <section className="text-gray-font font-sans flex flex-col gap-6 mx-1">
          <div className="grid grid-cols-12 gap-6 w-full mt-5">
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <Input
                title="Person Name"
                name="personName"
                error={false}
                errorMsj=""
                value={personName}
                onChange={(e) => setPersonName(e.target.value)}
                disabled
              />
            </div>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <Input
                title="Firm"
                name="firmName"
                error={false}
                errorMsj=""
                value={firmName}
                onChange={(e) => setFirmName(e.target.value)}
                disabled
              />
            </div>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <CalendarInput
                title="Invoice Date"
                type="single"
                error={false}
                errorMsj=""
                value={invoiceDate}
                onChange={(date) => setInvoiceDate(date || "")}
                disabled
              />
            </div>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <Input
                title="Reference"
                name="reference"
                error={false}
                errorMsj=""
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                disabled
              />
            </div>
            <div className="col-span-12 md:col-span-6 lg:col-span-3 flex gap-4 items-end justify-end">
              <Input
                title="Amount Paid (Event Payment)"
                name="amountPaid"
                type="number"
                placeholder="Add or subtract amount"
                value={paymentAdjustment}
                onChange={(e) => {
                  setPaymentAdjustment(e.target.value);
                  setPaymentAdjustmentError(false);
                  setPaymentAdjustmentErrorMsj("");
                }}
                error={paymentAdjustmentError}
                errorMsj={paymentAdjustmentErrorMsj}
              />
              <div className=" h-[52px]">
                <LegacyButton
                  text=""
                  content="textIcon"
                  color="accent"
                  icon={
                    <div className="w-6 h-6 mr-2">
                      <SaveChanges />
                    </div>
                  }
                  style="filled"
                  onClick={handleUpdateAmount}
                />
              </div>
            </div>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <Input
                title="Total Due"
                name="totalDue"
                error={false}
                errorMsj=""
                value={`${totalDue.toFixed(2)} ${event?.fee?.currency || payment?.currency || "USD"}`}
                disabled
              />
            </div>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <Input
                title="Email"
                name="email"
                type="email"
                error={false}
                errorMsj=""
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled
              />
            </div>
            <div className="col-span-12 md:col-span-6 lg:col-span-3"></div>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <Input
                title="CC Name"
                name="ccName"
                error={false}
                errorMsj=""
                value={ccName}
                onChange={(e) => setCcName(e.target.value)}
              />
            </div>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <Input
                title="CC Email"
                name="ccEmail"
                type="email"
                error={false}
                errorMsj=""
                value={ccEmail}
                onChange={(e) => setCcEmail(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-12 gap-6 w-full mt-5">
            <div className="col-span-12 md:col-span-6">
              <TextArea
                title="Bill To"
                name="billTo"
                error={false}
                errorMsj=""
                value={billTo}
                onChange={(e) => setBillTo(e.target.value)}
                height="min-h-[140px]"
                disabled
              />
            </div>
            <div className="col-span-12 md:col-span-6">
              <TextArea
                title="Remit To"
                name="remitTo"
                error={false}
                errorMsj=""
                value={remitTo}
                disabled
                height="min-h-[140px]"
              />
            </div>
          </div>
          <div className="flex flex-1 justify-end gap-[10px]">
            <LegacyButton
              text={
                payment?.status === "completed"
                  ? "PREVIEW CONFIRMATION"
                  : "PREVIEW INVOICE"
              }
              color="neutral"
              style="outlined"
              size="small"
              onClick={handlePreviewInvoice}
            />
            <LegacyButton
              text={
                payment?.status === "completed"
                  ? "SEND CONFIRMATION"
                  : "SEND INVOICE"
              }
              color="accent"
              size="small"
              onClick={handleSendInvoice}
            />
          </div>
        </section>
      </form>

      <div className="mt-5 mb-[6px] pl-[1px] pr-[1px]">
        <p className="font-decimal text-[32px] not-italic font-medium leading-normal text-Default">
          Email Invoice History
        </p>
      </div>
      {data ? (
        <div className="pl-[1px] pr-[1px]">
          <PaginatedTable
            data={data}
            columnsDef={invoiceHistoryColumns}
            formatData={formatData}
            sorting={sort}
            onSortingChange={setSort}
            totalCount={data.length}
            pagination={pagination}
            onPaginationChange={setPagination}
            showExcelExport={false}
          />
        </div>
      ) : (
        <div className="pl-[1px] pr-[1px]">
          <div className="flex p-4 items-start gap-2 self-stretch bg-gray-100">
            <p className="text-gray-font text-center font-sans text-[18px] not-italic font-semibold leading-normal flex-1">
              No emailed invoice found
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
